import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  fetchReplayConfiguration,
  fetchThankYouPage,
  fetchWaitingRoomPage,
  fetchWebinarById,
  fetchWebinarSessionConfiguration,
  fetchWebinarSubscriptionPage,
  listModerators,
  listOfferTemplates,
  listPresenters,
  listQuizTemplates,
  listSurveyTemplates,
  listWebinarResources,
  listWebinarSlides,
  listSharedResources,
} from '@/features/webinar/webinarThunks';
import {
  clearCurrentWebinar,
  setCreateWebinarInitialLoading,
} from '@/features/webinar/webinarSlice';

export default function CreateWebinarDataLoader() {
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const loadedWebinarId = useAppSelector((state) => state.webinar.currentWebinar?.id);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = webinarIdParam != null ? parseInt(String(webinarIdParam), 10) : undefined;
  const isValidId = typeof webinarId === 'number' && !isNaN(webinarId);

  // Lu via une ref pour ne pas relancer le chargement quand le webinaire arrive dans le store.
  const loadedWebinarIdRef = useRef(loadedWebinarId);
  useEffect(() => {
    loadedWebinarIdRef.current = loadedWebinarId;
  }, [loadedWebinarId]);

  useEffect(() => {
    if (!workspaceId || !isValidId) return;

    // Le webinaire précédemment ouvert est vidé du store avant de charger celui de l'URL, quel que
    // soit le point d'entrée : sinon ses quiz, sondages ou offres restent affichés tant que les
    // listes du nouveau ne sont pas revenues.
    if (loadedWebinarIdRef.current != null && loadedWebinarIdRef.current !== webinarId) {
      dispatch(clearCurrentWebinar());
    }

    let cancelled = false;
    dispatch(setCreateWebinarInitialLoading(true));
    Promise.allSettled([
      dispatch(fetchWebinarById({ workspaceId, id: webinarId })),
      dispatch(fetchWebinarSessionConfiguration({ workspaceId, webinarId })),
      dispatch(listPresenters({ workspaceId, webinarId })),
      dispatch(listModerators({ workspaceId, webinarId })),
      dispatch(fetchWebinarSubscriptionPage({ workspaceId, webinarId })),
      dispatch(fetchWaitingRoomPage({ workspaceId, webinarId })),
      dispatch(listWebinarSlides({ workspaceId, webinarId })),
      dispatch(listQuizTemplates({ workspaceId, webinarId })),
      dispatch(listSurveyTemplates({ workspaceId, webinarId })),
      dispatch(listOfferTemplates({ workspaceId, webinarId })),
      dispatch(listWebinarResources({ workspaceId, webinarId })),
      dispatch(listSharedResources({ workspaceId, webinarId })),
      dispatch(fetchReplayConfiguration({ workspaceId, webinarId })),
      dispatch(fetchThankYouPage({ workspaceId, webinarId })),
    ]).finally(() => {
      if (!cancelled) dispatch(setCreateWebinarInitialLoading(false));
    });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, webinarId, isValidId, dispatch]);

  return null;
}
